import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import ApiV2 from '../../../ApiV2'
import { RootState } from '../../../appReducer'
import PubNubService from '../../../services/PubNubService'
import { newObjectId } from '../../../util/objectId'

export interface OnSendMessageParams {
    channel: string
    message: string
    chatMetadata: Models.ChatMetadata
    messageId?: string
}
interface useMessagingReturn {
    onSendMessage: (onSendMessageParams: OnSendMessageParams) => Promise<unknown>
}

// Whisper is a specialist-internal side channel (AI suggestions, specialist-only notes) - never
// delivered to the patient and never part of admission's ledger, so it keeps publishing straight
// to PubNub as it always has. `53-`'s V1 site inventory named `useMessaging.tsx:42` as one call
// site without noticing it serves both this channel and the real conversation channel; routing a
// whisper send through admission gets refused outright (no chat record exists for `whisper_*`).
export const WHISPER_CHANNEL_PREFIX = 'whisper_'

const useMessaging = (): useMessagingReturn => {
    const conversations = useSelector((state: RootState) => state.chat.conversations)
    const account = useSelector((state: RootState) => state.app.self && state.app.self.account)

    const sendMessage = useCallback(
        (channel: string, message: string, messageId?: string) => {
            const conversation = conversations[channel]
            const messages = conversation?.messages
            const receivedWebchatCount = messages?.filter(msg => !msg.is_patient && msg.type === 'text').length ?? 0
            const newMessageId = messageId ?? newObjectId(32)

            if (channel.startsWith(WHISPER_CHANNEL_PREFIX)) {
                const pubnubService = PubNubService.getInstance(account?.id || 'none')
                return pubnubService.send(channel, {
                    id: newMessageId,
                    text: message,
                    is_patient: false,
                    sender_id: pubnubService.getChatterId(),
                    type: 'text',
                    sender_name: account?.firstName,
                    received_count: receivedWebchatCount,
                })
            }

            return ApiV2.Amplify.postLiveWebChatConversationEvent({
                channel,
                kind: 'specialist_turn',
                messageId: newMessageId,
                text: message,
                receivedCount: receivedWebchatCount,
            })
        },
        [account, conversations],
    )

    const onSendMessage = useCallback(
        (onSendMessageParams: OnSendMessageParams) => {
            const { channel, message, messageId } = onSendMessageParams

            return sendMessage(channel, message, messageId)
        },
        [sendMessage],
    )

    return { onSendMessage }
}

export default useMessaging
